import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import { api, errorMessage } from "../../lib/api";
import type { User } from "../../lib/types";
import { useAuth } from "../../lib/auth";
import { Modal } from "../core/Modal";
import { Button } from "../core/Button";
import { Field, Input, Select } from "../core/Field";

interface Props { open: boolean; onClose: () => void; lockedTenantId?: string; onCreated?: (u: User) => void }

export function UserFormModal({ open, onClose, lockedTenantId, onCreated }: Props) {
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const isBO = me?.user_type === "super_admin";
  const boOnly = isBO && !lockedTenantId;
  const empty = { name: "", email: "", staff_id: "", user_type: boOnly ? "super_admin" : "client_admin", role: "admin", tenant_id: lockedTenantId || "", password: "" };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [tempPw, setTempPw] = useState<string | null>(null);
  useEffect(() => {
    if (open) setForm(empty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lockedTenantId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: u } = await api.post("/users", { ...form, tenant_id: form.user_type === "super_admin" ? null : form.tenant_id || undefined, password: form.password || undefined });
      toast.success(`User ${u.email} created`);
      if (u.temporary_password) setTempPw(u.temporary_password);
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["tenants"] });
      qc.invalidateQueries({ queryKey: ["tenant"] });
      onCreated?.(u);
      onClose();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Modal open={open} onOpenChange={(o) => !o && onClose()} title={boOnly ? "Create back-office user" : "Create user"} testId="user-form-modal" description={boOnly ? "Operator back-office account. Client users are created from the client's page." : isBO ? "Client users belong to exactly one client." : "New user for your organisation."}
        footer={<><Button variant="secondary" onClick={onClose} data-testid="user-cancel-button">Cancel</Button><Button type="submit" form="user-form" loading={busy} data-testid="user-save-button">Create user</Button></>}>
        <form id="user-form" onSubmit={submit} className="space-y-3">
          <Field label="Full name"><Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Email" className="col-span-2"><Input data-testid="user-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></Field>
            <Field label="Staff ID" hint="Optional"><Input data-testid="user-staff-id-input" value={form.staff_id} onChange={(e) => setForm({ ...form, staff_id: e.target.value })} placeholder="e.g. EMP-0042" maxLength={40} className="font-mono" /></Field>
          </div>
          {boOnly && (
            <Field label="Back-office role" hint="Super Admin: everything · Finance: CRM, invoices (approve, send, mark paid) — no platform settings."><Select data-testid="user-bo-role-select" value={form.role === "finance" ? "finance" : "admin"} onChange={(e) => setForm({ ...form, role: e.target.value })}><option value="admin">Super Admin</option><option value="finance">Finance</option></Select></Field>
          )}
          {form.user_type === "client_admin" && (
            <Field label="Access level" hint="Admin: full control · Manager: runs the fleet, approves & hands over · Staff: view, book pool vehicles, submit requests."><Select data-testid="user-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}><option value="admin">Admin</option><option value="operator">Manager</option><option value="staff">Staff</option></Select></Field>
          )}
          <Field label="Password" hint="Leave blank to auto-generate a temporary password (min 8 chars)"><Input data-testid="user-password-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} minLength={8} /></Field>
        </form>
      </Modal>
      <Modal open={!!tempPw} onOpenChange={() => setTempPw(null)} title="Temporary password" testId="temp-password-modal" description="Share securely with the user. It will not be shown again.">
        <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 font-mono text-sm" data-testid="temp-password-value">{tempPw}<Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(tempPw || ""); toast.success("Copied"); }} data-testid="copy-temp-password-button"><Copy className="h-3.5 w-3.5" /></Button></div>
      </Modal>
    </>
  );
}

export function ResetPasswordModal({ target, onClose }: { target: User | null; onClose: () => void }) {
  const [newPw, setNewPw] = useState("");
  useEffect(() => { setNewPw(""); }, [target]);
  const reset = async () => {
    if (!target) return;
    try {
      await api.put(`/users/${target.id}`, { password: newPw });
      toast.success("Password reset");
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  return (
    <Modal open={!!target} onOpenChange={onClose} title={`Reset password · ${target?.email}`} testId="reset-password-modal"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={reset} disabled={newPw.length < 8} data-testid="reset-password-save-button">Set password</Button></>}>
      <Field label="New password"><Input data-testid="reset-password-input" type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} minLength={8} /></Field>
    </Modal>
  );
}
